/**
 * The column mapping, as data.
 *
 * `mapping[field]` names the file column a target field is read from: a header
 * string, or -- for free-text fields -- a list of headers joined in order. A
 * NLB personal export, for one, splits the other party's name and the payment
 * purpose over two columns, and the title wants both.
 *
 * columnsOf / compactMapping / joinValues mirror the functions of the same
 * name in backend staging/sources/file_import.py, so the preview on the
 * mapping screen is what the backend will store.
 */

export const AMOUNT_FIELDS = ['amount', 'debit', 'credit'];

// Used when talking to a backend that predates sniff's join_separator.
export const JOIN_SEPARATOR = ' - ';

// The order the mapping screen presents fields in: what must be there first,
// then what a row says, who it is with, how it is filed, and references last.
// A field the backend adds later lands in "Ostalo" rather than disappearing.
export const FIELD_GROUPS = [
  { key: 'required', title: 'Obvezno', fields: ['date_to_be_paid', 'amount', 'debit', 'credit'] },
  { key: 'text', title: 'Opis', fields: ['title', 'descr'] },
  {
    key: 'party',
    title: 'Druga stran',
    fields: ['counterparty_name', 'counterparty_iban', 'counterparty_tax_id'],
  },
  { key: 'filing', title: 'Razvrščanje', fields: ['category_name', 'tag_name'] },
  { key: 'refs', title: 'Reference', fields: ['invoice_number', 'external_ref'] },
];

// One line under a field's name, for the fields whose name alone does not say
// what the importer does with the value.
export const FIELD_HINTS = {
  title: 'Kratek naziv transakcije',
  descr: 'Daljši opis ali opomba',
  counterparty_name: 'Poveže vrstico s podjetjem ali osebo',
  counterparty_iban: 'Lastni račun pomeni prenos',
  counterparty_tax_id: 'Za iskanje podjetja',
  category_name: 'Le obstoječe kategorije',
  tag_name: 'Pot, npr. Vozila / kombi',
  invoice_number: 'Največ 32 znakov',
  external_ref: 'ID transakcije pri banki',
};

/** The columns one mapping entry names, in join order, blanks dropped. */
export const columnsOf = (value) => {
  if (value == null) return [];
  const items = Array.isArray(value) ? value : [value];
  return items.filter(c => c != null && String(c).trim()).map(String);
};

/**
 * What the pickers render: every slot, including one the user has added but
 * not filled yet. Never empty, so a field always shows at least one picker.
 */
export const slotsOf = (value) => {
  if (Array.isArray(value)) return value.length ? value : [null];
  return [value ?? null];
};

/** The sent and stored form: a string for one column, a list only when joined. */
export const compactMapping = (mapping) => Object.fromEntries(
  Object.entries(mapping || {})
    .map(([field, value]) => [field, columnsOf(value)])
    .filter(([, columns]) => columns.length)
    .map(([field, columns]) => [field, columns.length === 1 ? columns[0] : columns])
);

/** column -> the fields reading it, in mapping order. */
export const columnUsage = (mapping) => {
  const usage = new Map();
  Object.entries(mapping || {}).forEach(([field, value]) => {
    columnsOf(value).forEach((column) => {
      usage.set(column, [...(usage.get(column) || []), field]);
    });
  });
  return usage;
};

const text = (value) => (value == null ? '' : String(value).trim());

/** The first few non-empty values of a column, for recognising it by content. */
export const samplesOf = (column, rows, limit = 3) => (
  (rows || []).map(r => text(r?.[column])).filter(Boolean).slice(0, limit)
);

const joinKey = (value) => value.replace(/\s+/g, ' ').trim().toLowerCase();

// Built at runtime so the build leaves the unicode flag alone. Python's \w is
// Unicode-aware, and "ŽIGA J." has to count as a word here as it does there.
const WORD_CHAR = new RegExp('[\\p{L}\\p{N}_]', 'u');
const isWordChar = (ch) => Boolean(ch) && WORD_CHAR.test(ch);

/** Whether `needle` appears in `haystack` as whole words. */
const containsWords = (haystack, needle) => {
  for (let at = haystack.indexOf(needle); at >= 0; at = haystack.indexOf(needle, at + 1)) {
    if (!isWordChar(haystack[at - 1]) && !isWordChar(haystack[at + needle.length])) {
      return true;
    }
  }
  return false;
};

/**
 * Join cells into one text value without saying the same thing twice.
 *
 * A part equal to -- or, on whole words, contained in -- a part already kept
 * is dropped; one that contains a kept part takes its place. Compared with
 * whitespace collapsed and case ignored; written as the cell had it.
 */
export const joinValues = (values, separator = JOIN_SEPARATOR) => {
  let parts = [];
  (values || []).forEach((value) => {
    const cell = text(value);
    if (!cell) return;
    const key = joinKey(cell);
    if (parts.some(p => containsWords(p.key, key))) return;
    const swallowed = parts
      .map((p, i) => (containsWords(key, p.key) ? i : -1))
      .filter(i => i >= 0);
    if (swallowed.length) {
      parts[swallowed[0]] = { key, cell };
      parts = parts.filter((_, i) => !swallowed.slice(1).includes(i));
    } else {
      parts.push({ key, cell });
    }
  });
  return parts.length ? parts.map(p => p.cell).join(separator) : null;
};

/** What one row would put into `field`, as text -- or null. */
export const cellValue = (field, mapping, row, { joinable, separator } = {}) => {
  const columns = columnsOf(mapping?.[field]);
  if (!columns.length || !row) return null;
  if (joinable) return joinValues(columns.map(c => row[c]), separator);
  return text(row[columns[0]]) || null;
};

/**
 * Put `column` into `field` from outside the field's own pickers.
 *
 * An empty field takes it; a joinable one that already has columns adds it to
 * the end; any other replaces what it had. `assignAction` says which, so the
 * menu can say so before the user clicks.
 */
export const assignAction = (mapping, field, joinable) => {
  const current = columnsOf(mapping?.[field]);
  if (!current.length) return 'set';
  return joinable ? 'append' : 'replace';
};

export const assignColumn = (mapping, field, column, joinable) => {
  const current = columnsOf(mapping?.[field]);
  const action = assignAction(mapping, field, joinable);
  const next = { ...mapping };
  next[field] = action === 'append' ? [...current, column] : column;
  return next;
};

/**
 * Visible target fields, bucketed into FIELD_GROUPS order. Empty groups are
 * left out; unknown fields go to a trailing "Ostalo".
 */
export const groupFields = (fields) => {
  const byField = new Map(fields.map(f => [f.field, f]));
  const grouped = FIELD_GROUPS
    .map(g => ({ ...g, fields: g.fields.filter(f => byField.has(f)).map(f => byField.get(f)) }))
    .filter(g => g.fields.length);
  const known = new Set(FIELD_GROUPS.flatMap(g => g.fields));
  const rest = fields.filter(f => !known.has(f.field));
  if (rest.length) grouped.push({ key: 'other', title: 'Ostalo', fields: rest });
  return grouped;
};

/**
 * A saved profile's columns, with the fresh guess filling its gaps.
 *
 * The profile is authoritative for every field it names -- that is what it is
 * for. What it must NOT do is hide a target field that did not exist when it
 * was saved: a profile from before the tag column was supported names no tag,
 * and replacing the whole suggestion with it left every statement's tags
 * unread even with "create missing tags" ticked.
 *
 * A guess is skipped when the profile has already spoken for that column --
 * including as one part of a joined field -- so merging can never point two
 * fields at one column. The amount fields are left out entirely -- which of
 * them apply is decided by the profile's amount_shape, not by a per-field
 * guess.
 */
export const mergeWithProfile = (suggested, profileMapping) => {
  if (!profileMapping) return suggested || {};

  const spokenFor = new Set(Object.values(profileMapping).flatMap(columnsOf));
  const merged = { ...profileMapping };
  Object.entries(suggested || {}).forEach(([field, column]) => {
    if (field in merged) return;
    if (AMOUNT_FIELDS.includes(field)) return;
    if (columnsOf(column).some(c => spokenFor.has(c))) return;
    merged[field] = column;
  });
  return merged;
};
